/**
 * IdAustriaLinkDialog
 *
 * Just-in-Time account-linking dialog for ID Austria.
 *
 * Shown on /signin when the backend bounced the user back with
 * ?link_idaustria=<token>: the authenticated wbPK is NOT yet linked to any
 * account, so the user proves ownership of an existing account via
 * email + password. On success the wbPK is linked and a full login token is
 * returned — we store it exactly like a normal login and redirect to the
 * dashboard (or the optional return_to).
 *
 * POST /api/auth/oauth/id_austria/complete-link  { email, password, link_token }
 *   → { success, data: { user, token, refresh_token, organisation, return_to } }
 *   → 401 invalid credentials, 403 { mfa_required }, 409 id_austria_conflict
 */

'use client';

import { useState } from 'react';
import { Eye, EyeOff, LoaderCircleIcon, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertIcon, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { BackendAuth } from '@/lib/auth-backend';
import { useTranslation } from '@/hooks/useTranslation';

/**
 * Store the issued login token the same way BackendAuth.login() does, so the
 * rest of the app (cookie-based SSE, subdomain iframes, etc.) behaves identically
 * to a normal password login.
 */
function persistLoginSession({ token, user, domain }) {
  if (typeof window === 'undefined' || !token) return;
  // Defensive: wipe any stale per-user state from a previous session first.
  try {
    BackendAuth._clearUserBoundState?.();
  } catch {
    /* non-fatal */
  }
  localStorage.setItem('auth_token', token);
  if (user) localStorage.setItem('user_data', JSON.stringify(user));
  if (domain) localStorage.setItem('domain_data', JSON.stringify(domain));
  if (user?.email) localStorage.setItem('xoffi_last_login_email', user.email);

  // Mirror token into the auth cookie (SSE/EventSource + subdomain iframes).
  try {
    BackendAuth.syncTokenToCookie?.(token);
  } catch {
    /* non-fatal */
  }
}

/**
 * @param {object} props
 * @param {string} props.linkToken   the pending-link token from ?link_idaustria
 * @param {string} [props.returnTo]  optional internal path to land on after login
 * @param {() => void} props.onClose called when the user dismisses the dialog
 */
export default function IdAustriaLinkDialog({ linkToken, returnTo, onClose }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    if (!email.trim() || !password) {
      setError(
        t('auth.id_austria.error_missing_fields', {
          defaultValue: 'Bitte E-Mail und Passwort eingeben.',
          editable: false,
        }),
      );
      return;
    }

    setSubmitting(true);
    try {
      const result = await BackendAuth.apiCallJson(
        '/api/auth/oauth/id_austria/complete-link',
        {
          method: 'POST',
          body: JSON.stringify({
            email: email.trim(),
            password,
            link_token: linkToken,
            return_to: returnTo || undefined,
          }),
        },
      );

      if (result?.success && result?.data?.token) {
        persistLoginSession({
          token: result.data.token,
          user: result.data.user,
          domain: result.data.domain,
        });

        toast.success(
          t('auth.id_austria.linked_success', {
            defaultValue: 'ID Austria verknüpft und angemeldet.',
            editable: false,
          }),
        );

        const target =
          (result.data.return_to && result.data.return_to.startsWith('/')
            ? result.data.return_to
            : null) ||
          returnTo ||
          '/dashboard';

        setTimeout(() => {
          window.location.href = target;
        }, 150);
        return;
      }

      // 403 mfa_required is delivered as a 200-shaped body in some paths; handle
      // defensively here too.
      if (result?.mfa_required) {
        setError(
          t('auth.id_austria.error_mfa', {
            defaultValue:
              'Für dieses Konto ist 2FA aktiv. Die Verknüpfung per Passwort ist hier noch nicht möglich.',
            editable: false,
          }),
        );
        return;
      }

      setError(
        t('auth.id_austria.error_generic', {
          defaultValue: 'Verknüpfung fehlgeschlagen. Bitte erneut versuchen.',
          editable: false,
        }),
      );
    } catch (err) {
      const status = err?.status;
      if (status === 403 && err?.payload?.mfa_required) {
        setError(
          t('auth.id_austria.error_mfa', {
            defaultValue:
              'Für dieses Konto ist 2FA aktiv. Die Verknüpfung per Passwort ist hier noch nicht möglich.',
            editable: false,
          }),
        );
      } else if (status === 409 && err?.payload?.detail === 'id_austria_ambiguous') {
        setError(
          t('auth.id_austria.error_ambiguous', {
            defaultValue:
              'Mehrere Konten passen zu diesen Zugangsdaten. Bitte mit deinem Benutzernamen statt der E-Mail-Adresse anmelden.',
            editable: false,
          }),
        );
      } else if (status === 409) {
        setError(
          t('auth.id_austria.error_conflict', {
            defaultValue:
              'Diese ID Austria ist bereits mit einem anderen Konto verknüpft.',
            editable: false,
          }),
        );
      } else if (status === 401) {
        setError(
          t('auth.id_austria.error_invalid_credentials', {
            defaultValue: 'E-Mail oder Passwort ist falsch.',
            editable: false,
          }),
        );
      } else if (status === 400) {
        setError(
          t('auth.id_austria.error_expired', {
            defaultValue:
              'Die Verknüpfungs-Sitzung ist abgelaufen. Bitte erneut mit ID Austria anmelden.',
            editable: false,
          }),
        );
      } else {
        setError(
          t('auth.id_austria.error_generic', {
            defaultValue: 'Verknüpfung fehlgeschlagen. Bitte erneut versuchen.',
            editable: false,
          }),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !submitting) onClose?.();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <ShieldCheck className="w-6 h-6 text-primary" aria-hidden="true" />
            </div>
            <div>
              <DialogTitle>
                {t('auth.id_austria.link_dialog_title', 'Mit ID Austria verknüpfen')}
              </DialogTitle>
              <DialogDescription>
                {t(
                  'auth.id_austria.link_dialog_description',
                  'Zum Verknüpfen mit ID Austria bitte einmalig mit E-Mail und Passwort anmelden.',
                )}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2" noValidate>
          {error && (
            <Alert variant="destructive">
              <AlertIcon>
                <AlertCircle />
              </AlertIcon>
              <AlertTitle>{error}</AlertTitle>
            </Alert>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="id-austria-link-email">
              {t('auth.id_austria.email_label', 'Benutzername oder E-Mail')}
            </Label>
            <Input
              id="id-austria-link-email"
              type="text"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.id_austria.email_placeholder', {
                defaultValue: 'name@beispiel.at',
                editable: false,
              })}
              className="min-h-[44px]"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="id-austria-link-password">
              {t('auth.id_austria.password_label', 'Passwort')}
            </Label>
            <div className="relative">
              <Input
                id="id-austria-link-password"
                type={passwordVisible ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.id_austria.password_placeholder', {
                  defaultValue: 'Passwort',
                  editable: false,
                })}
                className="min-h-[44px] pr-12"
                required
              />
              <Button
                type="button"
                variant="ghost"
                mode="icon"
                size="sm"
                onClick={() => setPasswordVisible((v) => !v)}
                className="absolute end-0 top-1/2 -translate-y-1/2 h-9 w-9 me-1.5 bg-transparent!"
                aria-label={
                  passwordVisible
                    ? t('auth.id_austria.hide_password', {
                        defaultValue: 'Passwort verbergen',
                        editable: false,
                      })
                    : t('auth.id_austria.show_password', {
                        defaultValue: 'Passwort anzeigen',
                        editable: false,
                      })
                }
              >
                {passwordVisible ? (
                  <EyeOff className="text-muted-foreground" />
                ) : (
                  <Eye className="text-muted-foreground" />
                )}
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-2">
            <Button
              type="submit"
              disabled={submitting}
              className="w-full min-h-[44px]"
            >
              {submitting ? (
                <LoaderCircleIcon className="size-4 animate-spin mr-2" aria-hidden="true" />
              ) : null}
              {t('auth.id_austria.link_submit', 'Verknüpfen und anmelden')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={submitting}
              onClick={() => onClose?.()}
              className="w-full min-h-[44px]"
            >
              {t('common.buttons.cancel', 'Abbrechen')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
